/**
 * Plan State Module — stateless planning logic for Map-First Planning.
 *
 * This module manages the local plan state (localPlan) and provides:
 *  - CRUD operations for plan entries
 *  - Runner selection state
 *  - Event bus (onPlanChange) for decoupled UI updates
 *  - Submit payload builder
 *  - Client-side validation
 *  - JSON import/export (hidden power-user feature)
 *
 * NO DOM. NO rendering. All UI lives in map.ts / map/plan-ui.ts.
 */
import { fixture, mapData } from '../data/fixture';
import type { PlanEntryDTO, PlanCondition, ActionDTO } from '../data/fixture';
import { submitPlan, validatePlan } from '../api';
import type { PlanSubmitAction, ValidatePlanResponse } from '../api';
import { getJwt } from '../auth';
import { t } from '../i18n';

// Re-export for tests and external consumers
export type { ValidatePlanResponse };

// ── Constants ────────────────────────────────────────────────────────────────

export const CONDITION_TYPES = [
  'heat_below', 'heat_above', 'loy_above', 'loy_below',
  'cash_above', 'cash_below', 'hp_above', 'hp_below',
  'sector_owner', 'sector_unowned', 'runner_available',
  'day_is', 'day_before', 'day_after',
] as const;

const CONDITION_OPS: Record<string, string[]> = {
  heat_below: ['<', '<='], heat_above: ['>', '>='],
  loy_above: ['>', '>='], loy_below: ['<', '<='],
  cash_above: ['>', '>='], cash_below: ['<', '<='],
  hp_above: ['>', '>='], hp_below: ['<', '<='],
  sector_owner: ['==', '!='], sector_unowned: ['=='],
  runner_available: ['=='],
  day_is: ['=='], day_before: ['<', '<='], day_after: ['>', '>='],
};

// ── Undo state (single-step) ────────────────────────────────────────────────

let undoEntry: { entry: PlanEntryDTO; index: number } | null = null;
let undoTimer: ReturnType<typeof setTimeout> | null = null;

export function getUndoEntry(): { entry: PlanEntryDTO; index: number } | null { return undoEntry; }

export function performUndo(): boolean {
  if (!undoEntry) return false;
  const { entry, index } = undoEntry;
  localPlan.splice(Math.min(index, localPlan.length), 0, entry);
  planDirty = true;
  clearUndo();
  notify();
  return true;
}

function clearUndo(): void {
  undoEntry = null;
  if (undoTimer) { clearTimeout(undoTimer); undoTimer = null; }
}

// ── Plan Templates (localStorage) ──────────────────────────────────────────

const TEMPLATES_KEY = 'corpllm_plan_templates';
const MAX_TEMPLATES = 20;

export interface PlanTemplate {
  name: string;
  entries: Array<Omit<PlanEntryDTO, 'runner'>>;
  createdAt: string;
}

export function getTemplates(): PlanTemplate[] {
  try { return JSON.parse(localStorage.getItem(TEMPLATES_KEY) ?? '[]'); }
  catch { return []; }
}

export function saveTemplate(name: string): PlanTemplate {
  const templates = getTemplates();
  const tpl: PlanTemplate = {
    name: name.slice(0, 32),
    entries: localPlan.map(({ runner: _r, ...rest }) => rest),
    createdAt: new Date().toISOString(),
  };
  templates.unshift(tpl);
  if (templates.length > MAX_TEMPLATES) templates.length = MAX_TEMPLATES;
  localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  return tpl;
}

export function deleteTemplate(idx: number): void {
  const templates = getTemplates();
  if (idx >= 0 && idx < templates.length) {
    templates.splice(idx, 1);
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(templates));
  }
}

export function loadTemplate(idx: number, runnerId: string): boolean {
  const templates = getTemplates();
  const tpl = templates[idx];
  if (!tpl) return false;
  for (const entry of tpl.entries) {
    addPlanEntry({ runner: runnerId, ...entry });
  }
  return true;
}

export function exportTemplatesJSON(): string {
  return JSON.stringify(getTemplates(), null, 2);
}

export function importTemplatesJSON(json: string): { ok: boolean; count?: number; error?: string } {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return { ok: false, error: 'Not an array' };
    const valid = parsed.filter((t: any) => t.name && Array.isArray(t.entries)).slice(0, MAX_TEMPLATES);
    localStorage.setItem(TEMPLATES_KEY, JSON.stringify(valid));
    return { ok: true, count: valid.length };
  } catch (e) { return { ok: false, error: e instanceof Error ? e.message : String(e) }; }
}

// ── Mutable plan state ───────────────────────────────────────────────────────

let localPlan: PlanEntryDTO[] = [];
let planDirty = false;
let selectedRunnerId: string | null = null;
let selectedTeamName: string | null = null;  // mutually exclusive with selectedRunnerId

// ── Event bus ────────────────────────────────────────────────────────────────

type PlanChangeListener = () => void;
const listeners: PlanChangeListener[] = [];

/** Register a callback for any plan state change. Returns unsubscribe fn. */
export function onPlanChange(fn: PlanChangeListener): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx >= 0) listeners.splice(idx, 1);
  };
}

function notify(): void {
  for (const fn of listeners) fn();
}

// ── Getters ──────────────────────────────────────────────────────────────────

export function getLocalPlan(): readonly PlanEntryDTO[] { return localPlan; }
export function isPlanDirty(): boolean { return planDirty; }
export function getSelectedRunner(): string | null { return selectedRunnerId; }
export function getSelectedTeam(): string | null { return selectedTeamName; }

/** Get the active runner IDs for the current selection (single runner or all team members). */
export function getSelectedRunnerIds(): string[] {
  if (selectedTeamName) {
    const team = (fixture.player.teams ?? []).find(tm => tm.name === selectedTeamName);
    if (!team) return [];
    const runners = fixture.player.runners ?? [];
    // Resolve member names → runner IDs
    return team.members
      .map(name => runners.find(r => r.name === name)?.id)
      .filter((id): id is string => !!id);
  }
  if (selectedRunnerId) return [selectedRunnerId];
  return [];
}

/** Get plan entries for a specific runner. */
export function getRunnerPlan(runnerId: string): PlanEntryDTO[] {
  return localPlan.filter(e => e.runner === runnerId);
}

// ── Setters ──────────────────────────────────────────────────────────────────

/** Select a runner (shows their timeline, enables add). null = deselect. */
export function setSelectedRunner(runnerId: string | null): void {
  selectedRunnerId = runnerId;
  if (runnerId) selectedTeamName = null;  // mutually exclusive
  notify();
}

/** Select a team (context menu will expand actions to all members). null = deselect. */
export function setSelectedTeam(teamName: string | null): void {
  selectedTeamName = teamName;
  if (teamName) selectedRunnerId = null;  // mutually exclusive
  notify();
}

/** Get a display label for the current selection. */
export function getSelectionLabel(): string {
  if (selectedTeamName) {
    const team = (fixture.player.teams ?? []).find(tm => tm.name === selectedTeamName);
    return team ? `Team ${team.name} (${team.members.length})` : selectedTeamName;
  }
  if (selectedRunnerId) {
    const runner = (fixture.player.runners ?? []).find(r => r.id === selectedRunnerId);
    return runner?.name ?? selectedRunnerId;
  }
  return '';
}

/** Allow external screens (e.g. LLM advisor) to inject a full plan. */
export function setLocalPlan(plan: PlanEntryDTO[]): void {
  localPlan = plan;
  planDirty = true;
  notify();
}

/** Reset plan from fixture (e.g. on screen enter or WS state_update). */
export function resetPlanFromFixture(): void {
  planDirty = false;
  localPlan = (fixture.player.plan ?? []).map(e => ({ ...e, conditions: [...e.conditions] }));
  notify();
}

// ── CRUD ─────────────────────────────────────────────────────────────────────

/** Add a single plan entry. Priority = list position (appended at end = lowest). */
export function addPlanEntry(entry: Omit<PlanEntryDTO, 'conditions'> & {
  conditions?: PlanCondition[];
}): void {
  const full: PlanEntryDTO = {
    runner: entry.runner,
    action: entry.action,
    sector: entry.sector,
    conditions: entry.conditions ?? [],
  };
  if (entry.target) full.target = entry.target;
  if (entry.targetKind) full.targetKind = entry.targetKind;
  if (entry.targetRef) full.targetRef = entry.targetRef;
  if (entry.team) full.team = entry.team;

  localPlan.push(full);
  planDirty = true;
  notify();
}

/**
 * Add a team-level plan entry. Server expands to individual runner actions
 * and distributes across active members based on leader INT.
 */
export function addTeamPlanEntries(teamName: string, opts: {
  action: string;
  sector: string;
  target?: string;
}): void {
  const team = (fixture.player.teams ?? []).find(tm => tm.name === teamName);
  if (!team) return;

  // Use leader as the "runner" for display; server uses team field for expansion
  const runners = fixture.player.runners ?? [];
  const leader = runners.find(r => r.name === team.leader);
  const runnerId = leader?.id ?? runners[0]?.id ?? 'team';

  addPlanEntry({
    runner: runnerId,
    action: opts.action,
    sector: opts.sector,
    target: opts.target,
    team: teamName,
  });
}

/** Remove a plan entry by index. Stores entry for single-step undo (4s). */
export function removePlanEntry(idx: number): void {
  if (idx >= 0 && idx < localPlan.length) {
    clearUndo();
    const [removed] = localPlan.splice(idx, 1);
    undoEntry = { entry: removed, index: idx };
    undoTimer = setTimeout(() => { undoEntry = null; undoTimer = null; notify(); }, 4000);
    planDirty = true;
    notify();
  }
}

/** Update conditions on a plan entry by index. */
export function updatePlanEntryConditions(idx: number, conditions: PlanCondition[]): void {
  if (idx >= 0 && idx < localPlan.length) {
    localPlan[idx] = { ...localPlan[idx], conditions: [...conditions] };
    planDirty = true;
    notify();
  }
}

/** Set or clear the target-kind on a plan entry (#211 Ziel-Picker). An empty
 *  string clears it, reverting to the generic action-level target. */
export function updatePlanEntryTargetKind(idx: number, kind: string): void {
  if (idx < 0 || idx >= localPlan.length) return;
  const next = { ...localPlan[idx] };
  if (kind) next.targetKind = kind;
  else delete next.targetKind;
  localPlan[idx] = next;
  planDirty = true;
  notify();
}

/** Set or clear the target-ref — a specific entity, e.g. a cop patrol
 *  (`lawunit:<id>`) — on a plan entry (#213). Kind-specific: only meaningful
 *  for `cop_patrol`. An empty string clears it, reverting to the server's
 *  first-eligible-match behaviour. */
export function updatePlanEntryTargetRef(idx: number, ref: string): void {
  if (idx < 0 || idx >= localPlan.length) return;
  const next = { ...localPlan[idx] };
  if (ref) next.targetRef = ref;
  else delete next.targetRef;
  localPlan[idx] = next;
  planDirty = true;
  notify();
}

/** Reorder a plan entry from one index to another. Priority = list position. */
export function reorderPlanEntry(fromIdx: number, toIdx: number): void {
  if (fromIdx < 0 || fromIdx >= localPlan.length) return;
  if (toIdx < 0 || toIdx >= localPlan.length) return;
  if (fromIdx === toIdx) return;
  const [entry] = localPlan.splice(fromIdx, 1);
  localPlan.splice(toIdx, 0, entry);
  planDirty = true;
  notify();
}

/** Clear all entries. */
export function clearPlan(): void {
  localPlan = [];
  planDirty = true;
  notify();
}

// ── Action availability ─────────────────────────────────────────────────────

export interface ActionFilterContext {
  runnerId?: string;
  isOwned?: boolean;          // target building owned by player?
  targetType?: string;        // 'building' | 'node' | 'sector' | undefined
}

export interface ActionAvailability {
  available: boolean;
  reason?: string;            // i18n key for tooltip when unavailable
}

/**
 * Check whether an action is available given runner + context.
 * Returns { available, reason } — reason is an i18n key when unavailable.
 */
export function checkActionAvailability(action: ActionDTO, ctx: ActionFilterContext): ActionAvailability {
  const runner = ctx.runnerId
    ? (fixture.player?.runners ?? []).find(r => r.id === ctx.runnerId)
    : undefined;

  // Runner arrested → all actions disabled
  if (runner && runner.status === 'arrested') {
    return { available: false, reason: 'planui.filter_arrested' };
  }

  // Runner injured → all actions disabled
  if (runner && runner.status === 'injured') {
    return { available: false, reason: 'planui.filter_injured' };
  }

  // Target ownership mismatch
  if (action.targetOwnership && action.targetOwnership.length > 0 && ctx.isOwned !== undefined) {
    const has = ctx.isOwned ? 'own' : 'neutral';
    // 'rival' buildings appear as 'neutral' from the player's perspective (not owned)
    const matches = action.targetOwnership.some(o => o === has || (o === 'rival' && !ctx.isOwned));
    if (!matches) {
      return { available: false, reason: ctx.isOwned ? 'planui.filter_not_own' : 'planui.filter_not_rival' };
    }
  }

  // Target type mismatch (building vs node vs sector etc.)
  if (ctx.targetType && action.targetType?.length) {
    if (!action.targetType.includes(ctx.targetType)) {
      return { available: false, reason: 'planui.filter_wrong_target' };
    }
  }

  // prereqState checks (best-effort with available client data)
  if (action.prereqState?.length) {
    for (const ps of action.prereqState) {
      const c = ps.condition;
      if (c === 'has_nap_with_target') {
        const naps = fixture.player?.diplomacy?.naps ?? [];
        if (naps.length === 0) {
          return { available: false, reason: 'planui.filter_no_nap' };
        }
      }
      // Other prereqState conditions (target_kidnapped, target_owned_not_rented, etc.)
      // require a specific target selection — can't pre-filter without target, skip.
    }
  }

  // Future: prereqMinAttr, prereqSkill, maxPerCycle (server#202)

  return { available: true };
}

/**
 * Group actions by category and check availability for each.
 * Returns categories sorted alphabetically, each with sorted actions.
 * Empty categories (all actions unavailable) are excluded.
 */
export function getFilteredActionGroups(
  actions: ActionDTO[],
  ctx: ActionFilterContext,
  hideUnavailable = false,
): Array<{ cat: string; items: Array<{ action: ActionDTO; avail: ActionAvailability }> }> {
  const groups = new Map<string, Array<{ action: ActionDTO; avail: ActionAvailability }>>();

  for (const action of actions) {
    const avail = checkActionAvailability(action, ctx);
    if (hideUnavailable && !avail.available) continue;

    const cat = action.cat || '???';
    if (!groups.has(cat)) groups.set(cat, []);
    groups.get(cat)!.push({ action, avail });
  }

  return [...groups.entries()]
    .filter(([, items]) => items.some(i => i.avail.available))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([cat, items]) => ({ cat, items }));
}

// ── Action helpers ───────────────────────────────────────────────────────────

/** Determine if an action needs a target selector and what type. */
export function getTargetInfo(actionId: string): { show: boolean; type: string } {
  const actions = fixture.player.actions ?? [];
  const def = actions.find(a => a.id === actionId);
  if (!def?.targetType?.length) return { show: false, type: '' };
  const tt = def.targetType[0];
  if (tt === 'building' || tt === 'hood' || tt === 'player') return { show: true, type: tt };
  return { show: false, type: tt };
}

/** Build an array of target options for a given action. */
export function getTargetOptions(actionId: string): Array<{ group: string; value: string; label: string }> {
  const actions = fixture.player.actions ?? [];
  const def = actions.find(a => a.id === actionId);
  if (!def) return [];

  const targetInfo = getTargetInfo(actionId);
  const ownership = def.targetOwnership ?? [];
  const allowOwn = ownership.includes('own');
  const allowRival = ownership.includes('rival');
  const allowNeutral = ownership.includes('neutral');
  const noOwnership = ownership.length === 0;

  const opts: Array<{ group: string; value: string; label: string }> = [];

  if (targetInfo.type === 'building') {
    const buildings = fixture.player.buildings ?? [];
    if (allowOwn || noOwnership) {
      for (const b of buildings.filter(b => b.owned)) {
        opts.push({ group: 'own', value: b.nodeId, label: `${b.name} (${b.sector})` });
      }
    }
    if (allowRival || allowNeutral || noOwnership) {
      for (const b of buildings.filter(b => !b.owned)) {
        opts.push({ group: 'other', value: b.nodeId, label: `${b.name} (${b.sector})` });
      }
    }
  } else if (targetInfo.type === 'hood') {
    const runners = fixture.player.runners ?? [];
    if (allowOwn || noOwnership) {
      for (const r of runners) {
        opts.push({ group: 'own', value: r.id, label: `${r.name} (${r.id})` });
      }
    }
    if (allowRival || noOwnership) {
      for (const r of (fixture.player.diplomacy?.rivals ?? [])) {
        opts.push({ group: 'rival', value: r.corp, label: `[${r.corp}]` });
      }
    }
  } else if (targetInfo.type === 'player') {
    for (const r of (fixture.player.diplomacy?.rivals ?? [])) {
      opts.push({ group: 'rival', value: r.corp, label: r.corp });
    }
  }

  return opts;
}


// ── Validation ───────────────────────────────────────────────────────────────

export function buildValidationItems(plan: readonly PlanEntryDTO[]): { level: 'ok' | 'warn' | 'error'; text: string }[] {
  const runners = fixture.player.runners ?? [];
  const runnerIds = new Set(runners.map(r => r.id));
  const teams = fixture.player.teams ?? [];

  // Build assignedIds considering team expansion: team entries assign ALL active members.
  const assignedIds = new Set<string>();
  for (const e of plan) {
    if (e.team) {
      const team = teams.find(tm => tm.name === e.team);
      if (!team) continue;
      for (const memberName of team.members) {
        const r = runners.find(run => run.name === memberName);
        if (r) assignedIds.add(r.id);
      }
    } else if (e.runner) {
      assignedIds.add(e.runner);
    }
  }

  const items: { level: 'ok' | 'warn' | 'error'; text: string }[] = [];
  const runnerMap = new Map(runners.map(r => [r.id, r]));

  let invalidCount = 0;
  for (const entry of plan) {
    if (entry.team) continue; // team entries validated separately
    if (!runnerIds.has(entry.runner)) {
      items.push({ level: 'error', text: t('planning.validation_runner_missing', { id: entry.runner }) });
      invalidCount++;
    }
  }

  // Validate team entries
  for (const entry of plan) {
    if (!entry.team) continue;
    const team = teams.find(tm => tm.name === entry.team);
    if (!team) {
      items.push({ level: 'error', text: t('planning.validation_team_missing', { name: entry.team }) });
      invalidCount++;
    } else {
      const activeMembers = team.members
        .filter(name => runners.find(r => r.name === name && r.status === 'active'));
      if (activeMembers.length === 0) {
        items.push({ level: 'error', text: t('planning.validation_team_no_active', { name: entry.team }) });
        invalidCount++;
      }
    }
  }

  for (let i = 0; i < plan.length; i++) {
    const r = runnerMap.get(plan[i].runner);
    if (r && r.status === 'arrested') {
      items.push({ level: 'warn', text: t('planning.validation_runner_arrested', { idx: i + 1, name: r.name }) });
    }
  }

  const seenRunners = new Map<string, number>();
  for (let i = 0; i < plan.length; i++) {
    if (plan[i].team) continue; // team entries expand server-side; runner id is display-only
    const rid = plan[i].runner;
    if (!rid) continue;
    const prev = seenRunners.get(rid);
    if (prev !== undefined) {
      const r = runnerMap.get(rid);
      const name = r?.name ?? rid;
      items.push({ level: 'warn', text: t('planning.validation_runner_duplicate', { name, idx1: prev + 1, idx2: i + 1 }) });
    } else {
      seenRunners.set(rid, i);
    }
  }

  if (plan.length === 0) {
    items.push({ level: 'warn', text: t('planning.validation_empty') });
  } else if (invalidCount === 0) {
    items.push({ level: 'ok', text: t('planning.validation_ok', { count: plan.length }) });
  }

  const unassigned = runners.filter(r => r.status === 'active' && !assignedIds.has(r.id));
  if (unassigned.length > 0) {
    items.push({ level: 'warn', text: t('planning.validation_unassigned', { count: unassigned.length, names: unassigned.map(r => r.name).join(', ') }) });
  }

  // Condition operator validation
  for (let i = 0; i < plan.length; i++) {
    for (const cond of plan[i].conditions) {
      const validOps = CONDITION_OPS[cond.type];
      if (validOps && cond.op && !validOps.includes(cond.op)) {
        items.push({ level: 'error', text: t('planning.validation_invalid_op', { idx: i + 1, op: cond.op, type: cond.type }) });
        invalidCount++;
      }
    }
  }

  // Target ownership validation
  //
  // Classification priority:
  //   1. Player-owned buildings              → own
  //   2. Player runners                      → own
  //   3. Rival-corp markers (R_ prefix)      → rival
  //   4. Non-owned entry in player.buildings → neutral (observed city node)
  //   5. Any node in mapData.worldBuildings  → neutral (city catalog covers
  //      every buildable node; rivals are NOT surfaced on the client map,
  //      so from the player's view anything on the city map that's not
  //      owned is neutral — matches what actions like EXTORT expect)
  //   6. Unknown target                      → rival (fallback, bad data)
  //
  // Prior versions stopped at step 4 and fell straight to 'rival', which
  // blocked any plan targeting a city building the player hadn't yet
  // observed — e.g. picking a fresh EXTORT target from the map would
  // always fail validation with "Ziel X ist rival (erlaubt: neutral)".
  // See client#183.
  const allActions = fixture.player.actions ?? [];
  const ownBuildingNodes = new Set((fixture.player.buildings ?? []).filter(b => b.owned).map(b => b.nodeId));
  const ownRunnerIds = new Set(runners.map(r => r.id));
  const observedBuildingNodes = new Set((fixture.player.buildings ?? []).map(b => b.nodeId));
  const cityBuildingNodes = new Set((mapData?.worldBuildings ?? []).map(b => b.node_id));
  for (let i = 0; i < plan.length; i++) {
    const entry = plan[i];
    const actionDef = allActions.find(a => a.id === entry.action);
    if (!actionDef?.targetOwnership?.length || !entry.target) continue;

    const target = entry.target;
    let ownership: string | null = null;
    if (ownBuildingNodes.has(target)) ownership = 'own';
    else if (ownRunnerIds.has(target)) ownership = 'own';
    else if (target.startsWith('R_')) ownership = 'rival';
    else if (observedBuildingNodes.has(target)) ownership = 'neutral';
    else if (cityBuildingNodes.has(target)) ownership = 'neutral';
    else ownership = 'rival';

    if (ownership && !actionDef.targetOwnership.includes(ownership)) {
      items.push({
        level: 'error',
        text: t('planning.validation_target_ownership', { idx: i + 1, action: entry.action, target, ownership, allowed: actionDef.targetOwnership.join(', ') }),
      });
      invalidCount++;
    }
  }

  return items;
}

// ── Submit ────────────────────────────────────────────────────────────────────

export function buildSubmitPayload(): { hood_id: string; actions: PlanSubmitAction[] } {
  const runners = fixture.player.runners ?? [];
  const usedRunnerIds = new Set(localPlan.map(e => e.runner));
  const hoodId = runners.find(r => usedRunnerIds.has(r.id))?.id ?? runners[0]?.id ?? 'default';

  const actions: PlanSubmitAction[] = localPlan.map(e => ({
    runner: e.team ? undefined : e.runner, // team entries don't send runner
    team: e.team || undefined,
    command: e.action,
    target: e.target ?? e.sector,
    targetKind: e.targetKind || undefined,
    targetRef: e.targetRef || undefined,
    condition: e.conditions.length > 0 ? e.conditions : undefined,
    fallback: (e as any).else ?? undefined,
  }));

  return { hood_id: hoodId, actions };
}

export async function submitCurrentPlan(sessionId: string): Promise<{ ok: boolean; planId?: string; errors?: string[] }> {
  const jwt = getJwt();
  if (!jwt || !sessionId) return { ok: false, errors: [t('planning.no_session')] };

  const payload = buildSubmitPayload();
  try {
    const result = await submitPlan(sessionId, payload);
    if (result.ok) planDirty = false;
    return result;
  } catch (e) {
    return { ok: false, errors: [e instanceof Error ? e.message : String(e)] };
  }
}

export async function serverValidate(sessionId: string): Promise<ValidatePlanResponse> {
  // Expand team entries to one ValidateAction per active team member.
  // ValidateAction has no `team` field, so server sees only individual runners.
  const runners = fixture.player.runners ?? [];
  const teams = fixture.player.teams ?? [];
  const actions: { runner: string; action: string; sector: string; target?: string }[] = [];
  for (const e of localPlan) {
    if (e.team) {
      const team = teams.find(tm => tm.name === e.team);
      if (!team) continue;
      for (const memberName of team.members) {
        const r = runners.find(run => run.name === memberName && run.status === 'active');
        if (r) actions.push({ runner: r.id, action: e.action, sector: e.sector, target: e.target });
      }
    } else {
      actions.push({ runner: e.runner, action: e.action, sector: e.sector, target: e.target });
    }
  }
  return validatePlan(sessionId, actions);
}

// ── JSON import/export (power-user feature) ──────────────────────────────────

export function exportPlanJSON(): string {
  return JSON.stringify(localPlan, null, 2);
}

export function importPlanJSON(json: string): { ok: boolean; error?: string; count?: number } {
  try {
    const parsed = JSON.parse(json);
    if (!Array.isArray(parsed)) return { ok: false, error: t('planning.json_not_array') };
    localPlan = parsed as PlanEntryDTO[];
    planDirty = true;
    notify();
    return { ok: true, count: localPlan.length };
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

